/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import StatusChip from 'components/common/StatusChip';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RoleChip from 'components/common/RoleChip';
import _ from 'lodash';
import { CELL_TYPES } from '../foTableBuilder/FOCellTypes';
import { shortFormatDate } from '../../../shared/utils/dateUtils';
import { getSubmissionStatusId, SubmissionStatus } from '../../../models/SubmissionStatus';

const makeStyles = (theme) => {
  if (!theme) {
    return {};
  }
  return {
    draftState: {
      fontWeight: 1000,
      display: 'inline-flex',
      alignItems: 'center',
      marginRight: theme.spacing(0.5),
    },
    draftStatusIcon: {
      color: theme.palette.success.main,
      background: theme.palette.primary.contrastText,
      borderRadius: 50,
      marginRight: theme.spacing(0.5),
      marginLeft: theme.spacing(0.5),
    },
  };
};

const makeStatusTooltip = (theme, translate) => {
  const classes = makeStyles(theme);
  return (
    <div>
      <p>
        <span style={classes.draftState}>
          {translate('submission.status.draft')}
          :
        </span>
        {translate('minimum.requirements.not.met')}
      </p>
      <p>
        <span style={classes.draftState}>
          {translate('submission.status.draft')}
          <CheckCircleIcon style={classes.draftStatusIcon} />
          :
        </span>
        {translate('minimum.requirements.completed')}
      </p>
      <p>
        <span style={classes.draftState}>
          {translate('submission.status.ready.to.sign')}
          :
        </span>
        {translate('submission.ready.to.sign')}
      </p>
      <p>
        <span style={classes.draftState}>
          {translate('submission.status.signing.failed')}
          :
        </span>
        {translate('submission.signing.failed')}
      </p>
      {SubmissionStatus.READY_TO_PAY.visible && (
        <p>
          <span style={classes.draftState}>
            {translate('submission.status.ready.to.pay')}
            :
          </span>
          {translate('submission.ready.to.pay')}
        </p>
      )}
      {SubmissionStatus.READY_TO_PAY.visible && (
        <p>
          <span style={classes.draftState}>
            {translate('submission.status.paid')}
            :
          </span>
          {translate('submission.paid.description')}
        </p>
      )}
      <p>
        <span style={classes.draftState}>
          {translate('submission.status.packaging.failed')}
          :
        </span>
        {translate('submission.packaging.failed')}
      </p>
      <p>
        <span style={classes.draftState}>
          {translate('submission.status.ready.to.send')}
          :
        </span>
        {translate('submission.ready.for.sending')}
      </p>
      <p>
        <span style={classes.draftState}>
          {translate('submission.status.sending.failed')}
          :
        </span>
        {translate('submission.sending.failed')}
      </p>
      <p>
        <span style={classes.draftState}>
          {translate('submission.status.sent')}
          :
        </span>
        {translate('submission.has.been.sent')}
      </p>
    </div>
  );
};

function GenericCellDataAdapter(
  columnsConfiguration,
  frontendCustomisationConfig,
  flatRowData,
  rowErrors,
  findFirstApplicantName,
  translate,
  theme,
  combinedCellLabels,
  isExtraLargeOrBigger,
) {
  const roles = [];
  if (flatRowData)
    for (const key of Object.keys(flatRowData)) {
      if (key.includes('roles')) {
        roles.push(flatRowData[key]);
      }
    }

  const cellTypesAdapters = {
    selectAll: {
      align: 'left',
    },
    combinedCell: {
      label: combinedCellLabels || ['Actions'],
      align: 'left',
    },
    actionCell: {
      ariaLabel: isExtraLargeOrBigger ? 'actions.more' : 'Actions',
      label: isExtraLargeOrBigger ? 'actions.more' : 'Actions',
      align: 'center',
    },
    roles: {
      label: 'user.roles',
      value: roles?.length && (
        <>
          {roles.map(role => (
            <RoleChip role={role} />
          ))}
        </>
      ),
      filterType: 'roles',
      filterId: 'roles',
    },
    status: {
      label: 'submission.status',
      sortId: 'filter.statusOrder',
      value: flatRowData && (
        <StatusChip
          status={getSubmissionStatusId(flatRowData['filter.statusOrder'])}
          chipId={flatRowData.id}
          errorText={rowErrors}
        />
      ),
      filterId: 'status',
      filterType: 'status',
      tooltipContent: makeStatusTooltip(theme, translate),
    },
    invitationStatus: {
      label: 'invitations.status',
      sortId: 'filter.statusOrder',
      value: flatRowData && <StatusChip status={flatRowData.status} chipId={flatRowData.id} errorText={rowErrors} />,
      filterId: 'status',
      filterType: 'invitationStatus',
    },
    default: {
      align: 'left',
    },
  };
  const defaultTypesValuesAdapters = {
    firstApplicantName: submission => findFirstApplicantName(submission),
    modifiedDate: submission =>
      shortFormatDate(new Date(Date.parse(submission.modifiedDate)), frontendCustomisationConfig?.resource?.dateFormat),
  };

  let emptyFilterGroup = 1;
  return (
    _(columnsConfiguration)
      // eslint-disable-next-line no-plusplus
      .groupBy(config => config.filterGroup || emptyFilterGroup++)
      .sortBy(columnConfigs => columnsConfiguration.indexOf(columnConfigs[0]))
      .map((columnConfigs) => {
        let mergedWithDefaults = { filterIds: [], labels: [] };
        for (const columnConfig of columnConfigs) {
          mergedWithDefaults = { ...mergedWithDefaults, ...cellTypesAdapters[columnConfig.type], ...columnConfig };
          if (mergedWithDefaults.type === CELL_TYPES.DEFAULT && flatRowData) {
            if (columnConfig.translatedField) {
              mergedWithDefaults.value =
                mergedWithDefaults.value || translate(flatRowData[columnConfig.translatedField]);
            } else if (columnConfig?.conditions) {
              const _data = _.first(
                mergedWithDefaults?.conditions?.filter(item => window?.location?.href?.includes(item?.case?.url)),
              );
              mergedWithDefaults.value = mergedWithDefaults.value || flatRowData[_data.field];
            } else if (columnConfig.field) {
              mergedWithDefaults.value = mergedWithDefaults.value || flatRowData[columnConfig.field];
            } else if (columnConfig.computedValue && defaultTypesValuesAdapters[columnConfig.computedValue]) {
              mergedWithDefaults.value =
                mergedWithDefaults.value || defaultTypesValuesAdapters[columnConfig.computedValue](flatRowData);
            }
          }
          if (mergedWithDefaults.tooltipLabel) {
            mergedWithDefaults.tooltipContent = translate(mergedWithDefaults.tooltipLabel);
          }
          mergedWithDefaults.filterIds.push(mergedWithDefaults.filterId);
          mergedWithDefaults.labels.push(mergedWithDefaults.label);
        }
        return mergedWithDefaults;
      })
      .value()
  );
}

export default GenericCellDataAdapter;
